/**
 * CalendarWeek — faithful port of the canonical CalendarWeek screen.
 * 5-day work-week grid with time slots, event blocks, resource filter,
 * Day/Week/Month view switcher.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { SegmentedControl, alpha, Btn, Card, Checkbox, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { loadCalendarRange, blockSpanForDay, isScopeForbidden, type CalRangeBlock } from "./cal-range";
import { useT, t as i18nT } from "../../lib/i18n";
import { currentCalendarDate, friendlyCalendarHour, friendlyCalendarTime, friendlyCalendarWeekRange, friendlyCalendarWeekday, toParts } from "../../lib/datetime";

/* ── Calendar helpers ─────────────────────────────────────────────── */
const CAL_MS = 24 * 60 * 60 * 1000;

function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function weekStart(d: Date): Date { const x = new Date(d.getFullYear(),d.getMonth(),d.getDate()); x.setDate(x.getDate()-(((x.getDay()+6)%7))); return x; }
function isoWeek(d: Date): number { const t=new Date(Date.UTC(d.getFullYear(),d.getMonth(),d.getDate())); t.setUTCDate(t.getUTCDate()+4-(t.getUTCDay()||7)); const ys=new Date(Date.UTC(t.getUTCFullYear(),0,1)); return Math.ceil(((t.getTime()-ys.getTime())/CAL_MS+1)/7); }

/* ── Fictional calendar event seed ────────────────────────────────── */
interface CalEvent { id:string; title:string; dayOffset:number; start:number; end:number; color:string; type:string; loc?:string; }

const HOURS = [8,9,10,11,12,13,14,15,16,17,18,19,20]; // ui-config
const ROW_H = 48;
const TIME_W = 60;

export default function CalendarWeek() {
  const t = useT();
  const nav = useNav();
  const [weekOffset, setWeekOffset] = useState(0);
  const [calEvents, setCalEvents] = useState<CalEvent[] | null>(null);
  // B2 (2026-07-22 docket): anchor "today"/this-week on the tenant display tz,
  // not the browser's local zone (was `new Date()` at module scope) — otherwise
  // the week grid can highlight the wrong day near a tz/day boundary.
  const today = currentCalendarDate();
  const BASE_MON = weekStart(new Date(today.y, today.mo, today.d));
  // The filter's universe of types is derived straight from the events
  // actually on screen — never a hardcoded/static allowlist. A static list
  // can silently drift from the real category labels the API returns (that
  // was the bug: an event's category could never appear in the filter, so it
  // stayed permanently hidden, and "Clear" — which only reset to the same
  // stale allowlist — could never surface it again).
  const CAL_TYPES = useMemo(() => {
    const seen = new Map<string, string>(); // category label -> swatch colour (first event wins)
    for (const e of calEvents ?? []) if (!seen.has(e.type)) seen.set(e.type, e.color);
    return Array.from(seen, ([id, color]) => ({ id, label: id, color }));
  }, [calEvents]);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(() => new Set(CAL_TYPES.map((t)=>t.id)));
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const h = (e: MouseEvent) => { if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false); };
    document.addEventListener("mousedown",h);
    return () => document.removeEventListener("mousedown",h);
  },[filterOpen]);

  // Whenever a category appears that isn't in activeTypes yet (events load,
  // or a later fetch surfaces a label not seen before), default it to
  // visible — that gap is exactly what made "Clear" a no-op.
  useEffect(() => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const t of CAL_TYPES) if (!next.has(t.id)) { next.add(t.id); changed = true; }
      return changed ? next : prev;
    });
  }, [CAL_TYPES]);

  const ws = addDays(BASE_MON, weekOffset*7);
  // UIR-24 (Voyage #249): availability blocks from the typed calendar op — a
  // week that omits OOO/closures presents closed time as bookable.
  const [calBlocks, setCalBlocks] = useState<CalRangeBlock[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const from = new Date(ws); from.setHours(0,0,0,0);
    const to = new Date(ws); to.setDate(to.getDate()+5);
    let live = true;
    loadCalendarRange(from, to)
      .then(({ events, blocks }) => {
        if (!live) return;
        setLoadError(null);
        setCalEvents(events.map((e): CalEvent => {
          const sd = e.startsAt ? new Date(e.startsAt) : null;
          const ed = e.endsAt ? new Date(e.endsAt) : sd;
          // Position + label in the TENANT display tz (toParts), never the
          // browser's local zone (was sd.getHours()) -- an event stored at
          // 14:00 UTC / 10:00 America/New_York must sit in the 10:00 row, not
          // the 14:00 row (the calendar-week false-pass this closes).
          const sp = sd ? toParts(sd) : null;
          const ep = ed ? toParts(ed) : null;
          // Day-column assignment also runs off the TENANT calendar date (the
          // y/mo/d toParts gives), never a raw ms diff against `ws` -- an
          // event at 23:00 America/New_York (03:00 UTC the next day) belongs
          // in Monday's column, not Tuesday's.
          const dayOff = sp
            ? Math.round((Date.UTC(sp.y, sp.mo, sp.d) - Date.UTC(ws.getFullYear(), ws.getMonth(), ws.getDate())) / 86400000)
            : 0;
          return { id: e.id, title: e.title, dayOffset: dayOff, start: sp ? sp.h + sp.mi/60 : 9, end: ep ? ep.h + ep.mi/60 : 10, color: e.color, type: e.type, loc: e.loc };
        }).filter((e: CalEvent) => e.dayOffset >= 0 && e.dayOffset < 5));
        setCalBlocks(blocks);
      })
      .catch((err) => {
        if (!live) return;
        console.warn("getCalendar failed:", err);
        setCalEvents([]); setCalBlocks([]);
        setLoadError(isScopeForbidden(err)
          ? t("calendar.week.scopeForbidden", "Your role's scope doesn't cover this calendar range (SCOPE_FORBIDDEN) — nothing is shown.")
          : t("calendar.week.couldnTLoadTheCalendar", "Couldn't load the calendar. Retry in a moment."));
      });
    return () => { live = false; };
  }, [ws.getTime(), nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayDates = [0,1,2,3,4].map((i)=>addDays(ws,i)); // ui-config
  const [d0,d4] = [dayDates[0],dayDates[4]];
  const rangeLabel = friendlyCalendarWeekRange(d0, d4);

  const visible = (calEvents ?? []).filter((e)=> activeTypes.has(e.type));
  const hiddenCount = (calEvents ?? []).length - visible.length;


  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav,"calendar",useEventSectionCounts())} activeTab="events">
      {/* Toolbar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 24px", background:T.white, borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Btn variant="secondary" size="sm" onClick={() => nav.go("events")} icon={<Icon.chev dir="left" size={12}/>}>{t("calendar.week.events", "Events")}</Btn>
          <Btn variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>{t("calendar.week.today", "Today")}</Btn>
          <IconBtn label={t("calendar.week.previousWeek", "Previous week")} onClick={() => setWeekOffset(weekOffset-1)}><Icon.chev dir="left"/></IconBtn>
          <IconBtn label={t("calendar.week.nextWeek", "Next week")}     onClick={() => setWeekOffset(weekOffset+1)}><Icon.chev dir="right"/></IconBtn>
          <span style={{ fontSize:16, fontWeight:600, color:T.n1 }}>
            {rangeLabel} {t("calendar.week.week10e916fc", "· Week")}{" "}{isoWeek(ws)}
            {weekOffset!==0 ? <span onClick={() => setWeekOffset(0)} style={{ marginLeft:8, fontSize:12, fontWeight:500, color:T.p1, cursor:"pointer" }}>{t("calendar.week.jumpToCurrent", "Jump to current")}</span> : null}
          </span>
          {hiddenCount > 0 ? <Pill tone="warn"><Icon.filter size={10}/> {hiddenCount} {t("calendar.week.hidden", "hidden ·")}<span style={{ textDecoration:"underline", cursor:"pointer" }} onClick={() => setActiveTypes(new Set(CAL_TYPES.map((t)=>t.id)))}>{t("common.actions.clear", "Clear")}</span></Pill> : null}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {/* Type filter */}
          <div ref={filterRef} style={{ position:"relative" }}>
            <Btn variant="secondary" size="sm" icon={<Icon.filter size={14}/>} onClick={() => setFilterOpen((o)=>!o)}>
              {t("calendar.week.types", "Types ·")}{CAL_TYPES.filter((t) => activeTypes.has(t.id)).length}/{CAL_TYPES.length}
            </Btn>
            {filterOpen ? (
              <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:50, background:T.white, border:`1px solid ${T.border}`, borderRadius:8, boxShadow:T.shadowLg, padding:6, minWidth:200 }}>
                <div style={{ padding:"6px 10px 8px", fontSize:11, color:T.soft, fontWeight:600, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.week.eventTypes", "Event types")}</div>
                {CAL_TYPES.length === 0 ? <div style={{ padding:"8px 10px", fontSize:12.5, color:T.soft }}>{t("calendar.week.noEventsThisWeekYet", "No events this week yet.")}</div> : null}
                {CAL_TYPES.map((t) => (
                  <label key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:4, cursor:"pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background=T.n6)}
                    onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
                    <Checkbox checked={activeTypes.has(t.id)} onChange={() => { const s=new Set(activeTypes); s.has(t.id)?s.delete(t.id):s.add(t.id); setActiveTypes(s); }}/>
                    <span style={{ width:10, height:10, borderRadius:3, background:t.color, flexShrink:0 }}/>
                    <span style={{ fontSize:13, color:T.n1 }}>{t.label}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <IconBtn label={t("common.actions.refresh", "Refresh")} onClick={() => { setCalEvents(null); setNonce((n) => n + 1); }}><Icon.refresh/></IconBtn>
          <IconBtn label={t("calendar.week.settings", "Settings")} onClick={() => nav.go("settings.calendar")}><Icon.settings/></IconBtn>
          {/* View switcher */}
          <SegmentedControl<"day" | "week" | "month">
            size="md"
            ariaLabel="Calendar view"
            value="week"
            onChange={(v) => { if (v === "day") nav.go("calendar.day", { day: d0.getDate() });  if (v === "month") nav.go("calendar.month"); }}
            /* The three calendar views are configuration, not data. */
            options={[
              { value: "day", label: t("calendar.week.day", "Day") },
              { value: "week", label: t("calendar.week.week", "Week") },
              { value: "month", label: t("calendar.week.month", "Month") },
            ]}
          />
        </div>
      </div>

      {loadError ? <div style={{ padding:"8px 24px", background:alpha(T.red1,"12"), borderBottom:`1px solid ${alpha(T.red1,"33")}`, color:T.red1, fontSize:12.5, fontWeight:600, flexShrink:0 }}>{loadError}</div> : null}

      {/* Calendar grid */}
      <div style={{ flex:1, overflow:"auto", background:T.white }}>
        {/* Day headers */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, zIndex:10, background:T.white }}>
          <div/>
          {dayDates.map((d,i) => {
            const isToday = d.getFullYear()===today.y && d.getMonth()===today.mo && d.getDate()===today.d;
            return (
              <div key={i} onClick={() => nav.go("calendar.day",{ day:d.getDate(), month:d.getMonth(), year:d.getFullYear() })} style={{ padding:"10px 8px", textAlign:"center", borderLeft:`1px solid ${T.border}`, cursor:"pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background=T.n6)}
                onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
                <div style={{ fontSize:11, color:T.soft, fontWeight:600, textTransform:"uppercase" }}>{friendlyCalendarWeekday(d)}</div>
                <div style={{ marginTop:2, fontSize:16, fontWeight:600, color:isToday?T.white:T.n1, background:isToday?T.p1:"transparent", borderRadius:999, width:28, height:28, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* All-day strip */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, borderBottom:`1px solid ${T.border}`, background:T.n6 }}>
          <div style={{ padding:"6px 8px", textAlign:"right", fontSize:10, color:T.soft, textTransform:"uppercase", letterSpacing:0.04 }}>{t("calendar.week.allDay", "All day")}</div>
          {[0,1,2,3,4].map((i) => <div key={i} style={{ minHeight:28, borderLeft:`1px solid ${T.border}`, padding:4 }}/>)}
        </div>

        {/* Time grid */}
        <div style={{ display:"grid", gridTemplateColumns:`${TIME_W}px repeat(5, 1fr)`, position:"relative" }}>
          {/* Hour labels */}
          <div style={{ borderRight:`1px solid ${T.border}` }}>
            {HOURS.map((h) => (
              <div key={h} style={{ height:ROW_H, paddingTop:4, paddingRight:8, textAlign:"right", fontSize:11, color:T.soft, fontVariantNumeric:"tabular-nums" }}>
                {friendlyCalendarHour(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {[0,1,2,3,4].map((dayIdx) => {
            const dayEvs = visible.filter((e) => e.dayOffset === dayIdx);
            const daySpans = calBlocks
              .map((b) => ({ b, span: blockSpanForDay(b, dayDates[dayIdx], HOURS[0], HOURS[HOURS.length-1]+1) }))
              .filter((x): x is { b: CalRangeBlock; span: { startH: number; endH: number } } => x.span !== null);
            return (
              <div key={dayIdx} style={{ position:"relative", borderLeft:`1px solid ${T.border}` }}>
                {HOURS.map((h) => <div key={h} style={{ height:ROW_H, borderBottom:`1px solid ${alpha(T.border,"80")}` }}/>)}
                {daySpans.map(({ b, span }) => {
                  const top = (span.startH - HOURS[0]) * ROW_H;
                  const height = (span.endH - span.startH) * ROW_H;
                  const who = b.scope === "all-staff" ? t("calendar.week.allStaff", "All staff") : (b.staffName || t("calendar.week.staff", "Staff"));
                  return (
                    <div key={`${b.id}-${dayIdx}`} title={/* TODO(i18n): replace template-literal with t('calendar.week.title', { ... }) */`${b.title} · ${who}${b.blockType ? ` · ${b.blockType}` : ""}`}
                      style={{ position:"absolute", left:2, right:2, top, height:Math.max(height-2,18), borderRadius:4, background:`repeating-linear-gradient(-45deg, ${alpha(T.n3,"2E")} 0px, ${alpha(T.n3,"2E")} 5px, ${alpha(T.n3,"14")} 5px, ${alpha(T.n3,"14")} 10px)`, border:`1px dashed ${T.n4}`, padding:"3px 7px", overflow:"hidden", zIndex:1 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:T.n2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.title}</div>
                      {height > 34 ? <div style={{ fontSize:10.5, color:T.soft, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{who}{b.blockType ? /* TODO(i18n): replace template-literal with t('calendar.week.text', { ... }) */` · ${b.blockType}` : ""}</div> : null}
                    </div>
                  );
                })}
                {dayEvs.map((e) => {
                  const top = (e.start - HOURS[0]) * ROW_H;
                  const height = (e.end - e.start) * ROW_H;
                  return (
                    <div key={e.id} onClick={() => toast({ tone:"info", title:e.title, subtitle:i18nT("calendar.week.fmthmEStartFmthmEEndELoc.icu", { value0: friendlyCalendarTime(e.start), value1: friendlyCalendarTime(e.end), value2: e.loc??"" })})}
                      style={{ position:"absolute", left:4, right:4, top, height:Math.max(height-2,20), borderRadius:6, background:alpha(e.color,"22"), borderLeft:`3px solid ${e.color}`, padding:"3px 7px", cursor:"pointer", overflow:"hidden", zIndex:2 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:e.color, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{e.title}</div>
                      {height > 32 ? <div style={{ fontSize:11, color:T.soft }}>{friendlyCalendarTime(e.start)} – {friendlyCalendarTime(e.end)}{e.loc ? /* TODO(i18n): replace template-literal with t('calendar.week.text2', { ... }) */` · ${e.loc}` : ""}</div> : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </Screen>
  );
}
